// ---------------------------------------------------------------------------
// ONE presenter for every withheld client-facing strategy surface.
//
// The withholding DECISION is the backend's and is never taken here. This maps
// the CANONICAL reason the backend already published — blocker codes, release
// disposition, propagation state — onto client-safe language, in one place
// instead of scattered conditionals that drifted apart and contradicted each
// other and the evidence they were describing.
//
// Three rules the copy must never break:
//
//   1. Never say REGENERATION REQUIRED when requiresRegeneration is false.
//   2. Never describe an allocation blocker as a generation failure.
//   3. Never describe a SETTLED operation as work in progress. A failed
//      propagation obligation is finished, not ongoing — telling a client
//      "your Cphere team is completing the update" about a terminally failed
//      operation is a statement nobody can act on and nobody is acting on.
//
// It also discloses nothing internal: no operation ids, hashes, failure
// classes, prompts, findings or diagnostics reach this text.
// ---------------------------------------------------------------------------

export const PUBLIC_WITHHOLDING_REASONS = Object.freeze({
  AWAITING_APPROVAL: "AWAITING_APPROVAL",
  AWAITING_AUTHORIZATION: "AWAITING_AUTHORIZATION",
  ALLOCATION_REQUIRED: "ALLOCATION_REQUIRED",
  REVISION_REQUIRED: "REVISION_REQUIRED",
  REVISION_ATTENTION: "REVISION_ATTENTION",
  HISTORICAL_BINDING_UNVERIFIABLE: "HISTORICAL_BINDING_UNVERIFIABLE",
  UNAVAILABLE: "UNAVAILABLE",
});

const COPY = Object.freeze({
  AWAITING_APPROVAL: {
    statusLabel: "AWAITING APPROVAL",
    title: "Strategy awaiting approval",
    message:
      "This campaign's strategy is complete and awaiting approval. It will appear here once it has been approved and authorized for sharing.",
  },
  AWAITING_AUTHORIZATION: {
    statusLabel: "AWAITING AUTHORIZATION",
    title: "Strategy awaiting authorization",
    message:
      "This campaign's strategy has been approved and is awaiting written authorization before it can be shared here.",
  },
  ALLOCATION_REQUIRED: {
    statusLabel: "CHANNEL ALLOCATION PENDING",
    title: "Channel allocation pending",
    message:
      "This strategy's channel allocation is being finalized. The strategy will be available here once the allocation is approved.",
  },
  REVISION_REQUIRED: {
    statusLabel: "REVISION IN PROGRESS",
    title: "Strategy revision in progress",
    message:
      "This campaign's details changed after the strategy was written, so the strategy is being revised. It will appear here once the revised strategy is approved.",
  },
  // Deliberately distinct from REVISION_REQUIRED: the last attempt SETTLED
  // without producing a revision. Nothing is currently running, so the copy
  // must not imply anyone is mid-task.
  REVISION_ATTENTION: {
    statusLabel: "REVISION REQUIRED",
    title: "Strategy revision required",
    message:
      "This campaign's details changed after the strategy was written. The strategy is withheld until a revised version has been prepared and approved.",
  },
  UNAVAILABLE: {
    statusLabel: "STRATEGY UNAVAILABLE",
    title: "Strategy unavailable",
    message:
      "This campaign's strategy is not available to view yet. It will appear here once it is approved and authorized for sharing.",
  },
  HISTORICAL_BINDING_UNVERIFIABLE: {
    statusLabel: "STRATEGY VERIFICATION REQUIRED",
    title: "Historical strategy verification required",
    message:
      "This strategy's historical dependency evidence is unavailable. Its currency has not been established; this does not establish that campaign details changed. The strategy remains withheld until the required governance evidence and approvals are established.",
  },
});

const upper = (value) => String(value == null ? "" : value).trim().toUpperCase();

const blockerCodes = (governance) =>
  (Array.isArray(governance?.blockers) ? governance.blockers : [])
    .map((blocker) => upper(blocker?.code))
    .filter(Boolean);

// A propagation obligation that FAILED — or any settled/converged state — is
// finished. Only a genuinely running obligation is "in progress".
const propagationIsRunning = (governance) => {
  const state = upper(governance?.propagation?.state);
  if (!state) return false;
  if (state === "UPDATE_IN_PROGRESS") return true;
  const obligation = upper(governance?.propagation?.obligationStatus);
  return state !== "UPDATE_FAILED" && state !== "CONVERGED" && obligation !== "FAILED";
};

/**
 * Resolve the canonical public-safe withholding reason.
 *
 * Order matters: the reason a client can ACT on outranks the reason that is
 * merely true. An outstanding allocation decision is actionable; "the inputs
 * changed" is not.
 */
export const resolvePublicWithholdingReason = (governance = null) => {
  if (!governance || typeof governance !== "object") {
    return PUBLIC_WITHHOLDING_REASONS.UNAVAILABLE;
  }

  const codes = blockerCodes(governance);
  const requiresRegeneration = governance.requiresRegeneration === true;
  const disposition = upper(governance.releaseDisposition);
  const status = upper(governance.status);

  // A missing historical binding is not evidence of changed campaign inputs.
  // Read the actual authority, not the legacy regeneration status alone.
  // The audience sanitizer exposes reasonCode but strips the internal proof.
  if (governance.reasonCode === "LEGACY_BINDING_UNVERIFIABLE" ||
      (governance.bindingDecision?.decision === "LEGACY_BINDING_UNVERIFIABLE" &&
      governance.bindingDecision?.aiRequired === false &&
      Array.isArray(governance.bindingDecision.changedFields) &&
      governance.bindingDecision.changedFields.length === 0)) {
    return PUBLIC_WITHHOLDING_REASONS.HISTORICAL_BINDING_UNVERIFIABLE;
  }

  if (
    codes.includes("GOVERNED_CHANNEL_ALLOCATION_REQUIRED") ||
    codes.includes("ALLOCATION_APPROVAL_REQUIRED") ||
    status === "ALLOCATION_APPROVAL_REQUIRED"
  ) {
    return PUBLIC_WITHHOLDING_REASONS.ALLOCATION_REQUIRED;
  }

  // Approval-only withholding. Guarded on requiresRegeneration so the copy can
  // never contradict the evidence it is describing.
  if (
    !requiresRegeneration &&
    (disposition === "WITHHELD_APPROVAL_REQUIRED" ||
      status === "AWAITING_APPROVAL" ||
      codes.includes("APPROVAL_REQUIRED"))
  ) {
    return PUBLIC_WITHHOLDING_REASONS.AWAITING_APPROVAL;
  }

  if (!requiresRegeneration && governance.approved === true) {
    return PUBLIC_WITHHOLDING_REASONS.AWAITING_AUTHORIZATION;
  }

  if (requiresRegeneration || disposition === "WITHHELD_DEPENDENCY_STALE") {
    return propagationIsRunning(governance)
      ? PUBLIC_WITHHOLDING_REASONS.REVISION_REQUIRED
      : PUBLIC_WITHHOLDING_REASONS.REVISION_ATTENTION;
  }

  return PUBLIC_WITHHOLDING_REASONS.UNAVAILABLE;
};

export const resolvePublicWithholdingCopy = (governance = null) => {
  const reason = resolvePublicWithholdingReason(governance);
  return { reason, ...COPY[reason] };
};

export const PUBLIC_WITHHOLDING_COPY = COPY;
